import React, { forwardRef } from 'react';
import {
  FormControl,
  Box,
  Typography,
  styled,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import CustomButton from '../Button/CustomButton';
import { StyledFormHelperText } from "../TextField/StyledFormHelperText";
import IconReusable from "../../../components/IconReusable";

// Helper function to safely check if a value is a valid Day.js object
const isDayjsObject = (value) => {
  return value && typeof value === 'object' && typeof value.isValid === 'function';
};

// Helper function to validate a date value
const isValidDate = (value) => {
  if (!value) return true; // null/undefined is considered valid (empty state)
  return isDayjsObject(value) && value.isValid();
};

const StyledFormControl = styled(FormControl)(({ theme, error, variant = 'outlined' }) => ({
  width: '100%',
  fontFamily: 'Lato, sans-serif',
  
  '& .MuiFormLabel-root': {
    fontFamily: 'Lato, sans-serif',
    fontSize: '13px',
    fontWeight: 700,
    color: '#333333',
    lineHeight: '20px',
    marginBottom: '8px',
    transform: 'none',
    position: 'static',
    '&.Mui-focused': {
      color: '#333333',
    },
    '&.Mui-error': {
      color: '#333333',
    },
  },
}));

const StyledDatePicker = styled(MuiDatePicker)(({ theme, error, variant = 'outlined' }) => ({
  fontFamily: 'Lato, sans-serif',
  width: '100%',
  
  // TextField styling to match Figma
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Lato, sans-serif',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: '#333333',
    height: '40px',
    borderRadius: '8px',
    border: `1px solid ${error ? '#CC0000' : '#8C8C8C'}`,
    
    '& fieldset': {
      border: 'none', // Remove default MUI border
    },
    
    '&:hover': {
      borderColor: error ? '#CC0000' : '#333333',
      '& fieldset': {
        border: 'none',
      },
    },
    
    '&.Mui-focused': {
      borderColor: error ? '#CC0000' : '#333333',
      borderWidth: '2px',
      outline: 'none',
      boxShadow: 'none',
      '& fieldset': {
        border: 'none',
      },
    },
    
    '&.Mui-disabled': {
      backgroundColor: '#F5F5F5',
      borderColor: '#D0D0D0',
      color: '#8C8C8C',
      '& fieldset': {
        border: 'none',
      },
    },
  },
  
  '& .MuiInputBase-input': {
    padding: '8px 12px',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    height: '24px', 
    
    '&::placeholder': {
      color: '#8C8C8C',
      opacity: 1,
      fontWeight: 400,
    },
    
    '&:focus': {
      outline: 'none',
    },
  },
  '& .MuiInputAdornment-root': {
    marginLeft: '0',
    marginRight: '12px',
    display: 'flex !important',
    '& button': {
      display: 'flex !important',
    },
    '& .MuiSvgIcon-root': {
      color: error ? '#CC0000' : '#8C8C8C',
      fontSize: '20px',
    },
  },
  '& .MuiInputLabel-root': {
    display: 'none',
  },
  '&.Mui-disabled': {
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: '#D0D0D0',
    },
  },
}));

// const StyledFormHelperText = styled(FormHelperText)(({ theme, error }) => ({
//   fontFamily: 'Lato, sans-serif',
//   fontSize: '13px',
//   fontWeight: 400,
//   lineHeight: '20px',
//   color: error ? '#CC0000' : '#8C8C8C',
//   marginTop: '8px',
//   marginLeft: 0,
//   marginRight: 0,
// }));

const StyledDescription = styled(Box)(({ theme }) => ({
  fontFamily: 'Lato, sans-serif',
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '20px',
  color: '#8C8C8C',
  marginBottom: '8px',
}));

const DatePicker = forwardRef(
  (
    {
      label,
      description,
      helperText,
      error = false,
      variant = "outlined",
      placeholder = "DD/MM/YYYY",
      selectableDate = null,
      value,
      onChange,
      onError,
      disabled = false,
      required = false,
      fullWidth = true,
      minDate,
      maxDate,
      disablePast = false,
      disableFuture = false,
      format = "DD/MM/YYYY",
      sx,
      slotProps,
      ...props
    },
    ref,
  ) => {
    const isMobile = useMediaQuery("(pointer: coarse)");
    // Check if the current value is valid using helper function
    const isValueValid = isValidDate(value);
    const hasValidationError = !isValueValid || error;

    const handleDateChange = (newValue, context) => {
      // Call the original onChange
      if (onChange) {
        onChange(newValue, context);
      }

      // Handle validation errors
      if (onError && newValue) {
        const isValid = isValidDate(newValue);
        if (!isValid) {
          onError("Invalid date format");
        } else {
          onError(null); // Clear error
        }
      }
    };

  const handleDateError = (error, value) => {
    if (onError) {
      let errorMessage = null;
      
      switch (error) {
        case 'invalidDate':
          errorMessage = 'Please enter a valid date';
          break;
        case 'minDate':
          errorMessage = `Date must be after ${minDate?.format(format)}`;
          break;
        case 'maxDate':
          errorMessage = `Date must be before ${maxDate?.format(format)}`;
          break;
        case 'disablePast':
          errorMessage = 'Past dates are not allowed';
          break;
        case 'disableFuture':
          errorMessage = 'Future dates are not allowed';
          break;
        default:
          errorMessage = error ? 'Invalid date' : null;
      }
      
      onError(errorMessage);
    }
  };

  return (
    <StyledFormControl
      error={hasValidationError}
      variant={variant}
      fullWidth={fullWidth}
      sx={sx}>
      {label && (
        <Typography
          sx={{
            color: error ? "#CC0000" : "#666",
            fontSize: "13px",
            lineHeight: "20px",
            fontWeight: "700",
          }}
          component="legend"
          required={required}>
          {label}
        </Typography>
      )}

      {description && <StyledDescription>{description}</StyledDescription>}

      <StyledDatePicker
        ref={ref}
        error={hasValidationError}
        variant={variant}
        value={value}
        onChange={handleDateChange}
        onError={handleDateError}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        disablePast={disablePast}
        disableFuture={disableFuture}
        format={format}
        slots={{
          openPickerIcon: () => (
            <IconReusable
              name="Calendar"
              fill={hasValidationError ? "#CC0000" : "#666"}
            />
          ),
          toolbar: ({ value, onChange }) => (
            <Box
              sx={{
                padding: "16px",
                pb: "8px",
                borderBottom: "1px solid #D9D9D9",
              }}>
              <Typography variant="labelD" sx={{ pb: 1 }}>
                Select Date
              </Typography>
              <Typography variant="h2">
                {value
                  ? value.format("ddd, MMM DD")
                  : selectableDate
                    ? selectableDate.format("ddd, MMM DD")
                    : "No date selected"}
              </Typography>
            </Box>
          ),
          actionBar: ({ onSetToday, onCancel, onAccept }) => (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: "12px",
                pb: "8px",
                pt: "4px",
              }}>
              <CustomButton
                variant="text"
                color="primary"
                label="Today"
                onClick={onSetToday}
              />

              <Box sx={{ display: "flex", gap: "8px" }}>
                <CustomButton
                  variant="text"
                  color="primary"
                  label="Cancel"
                  onClick={onCancel}
                />

                <CustomButton
                  variant="text"
                  color="primary"
                  label="OK"
                  onClick={onAccept}
                  sx={{ display: "block" }}
                />
              </Box>
            </Box>
          ),
        }}
        slotProps={{
          toolbar: {
            hidden: false,
          },
          textField: {
            variant: "outlined",
            error: hasValidationError,
            placeholder: !value ? placeholder : undefined,
            InputLabelProps: {
              shrink: false,
            },
            ...(isMobile && {
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconReusable
                      name="Calendar"
                      width={20}
                      height={20}
                      fill={hasValidationError ? "#CC0000" : "#666"}
                    />
                  </InputAdornment>
                ),
              },
            }),
            ...slotProps?.textField,
          },
          openPickerButton: {
            sx: {
              display: "flex !important",
            },
          },
          desktopPaper: {
            sx: {
              boxShadow: "0px 2px 8px 0px #0000001A",
              borderRadius: "16px",
              overflow: "hidden",
              fontFamily: "Lato, sans-serif",
              "& .MuiPickersLayout-root": {
                display: "flex",
                flexDirection: "column",
              },
              "& .MuiDateCalendar-root": {
                order: 2,
                width: "320px",
                maxHeight: "none",
                overflow: "visible",
                backgroundColor: "#FFFFFF",
                height: "320px",
              },

              "& .MuiYearCalendar-root": {
                height: "250px",
              },
              "& .MuiPickersLayout-actionBar": {
                order: 3,
              },

              "& .MuiPickersCalendarHeader-root": {
                paddingBottom: "8px",

                "& .MuiPickersCalendarHeader-label": {
                  fontFamily: "Lato, sans-serif",
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#333333",
                },
                "& .MuiPickersCalendarHeader-labelContainer": {
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                },

                "& .MuiPickersCalendarHeader-switchViewButton": {
                  color: "#8C8C8C",
                  "&:hover": {
                    backgroundColor: "#F5F5F5",
                    color: "#333333",
                  },
                },

                "& .MuiPickersArrowSwitcher-button": {
                  color: "#8C8C8C",
                  "&:hover": {
                    backgroundColor: "#F5F5F5",
                    color: "#333333",
                  },
                },
              },
              "& .MuiDayCalendar-header": {
                "& .MuiDayCalendar-weekDayLabel": {
                  fontFamily: "Lato, sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#8C8C8C",
                  textTransform: "uppercase",
                },
              },
              "& .MuiPickersDay-root": {
                fontFamily: "Lato, sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#333333",
                backgroundColor: "transparent",
                border: "none",

                // Selected date styling
                "&.Mui-selected": {
                  backgroundColor: "#995A00",
                  color: "#FFFFFF",
                  fontWeight: 600,

                  "&:hover": {
                    backgroundColor: "#995A00",
                  },
                },

                // Hover state for non-selected days
                "&:hover:not(.Mui-selected)": {
                  backgroundColor: "#D9D9D9",
                },
                "&.MuiPickersDay-today:not(.Mui-selected)": {
                  backgroundColor: "transparent",
                  color: "#995A00",
                  fontWeight: 600,
                  border: "1px solid #995A00",
                },
                "&.Mui-disabled": {
                  color: "#D0D0D0",
                  backgroundColor: "transparent",
                },
              },
            },
          },
          mobilePaper: {
            sx: {
              "& .MuiPickersCalendarHeader-labelContainer": {
                display: "flex !important",
                justifyContent: "center",
                alignItems: "center",
              },
              "& .MuiPickersLayout-root": {
                display: "flex !important",
                flexDirection: "column !important",
                width: "100%",
              },
            },
          },
          popper: {
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  rootBoundary: 'viewport',
                  tether: false,
                  altAxis: true,
                  padding: 8,
                },
              },
              {
                name: 'flip',
                options: {
                  rootBoundary: 'viewport',
                },
              },
            ],
          },
          ...slotProps,
        }}
        {...props}
      />

      {helperText && (
        <StyledFormHelperText error={hasValidationError}>
          {helperText}
        </StyledFormHelperText>
      )}
    </StyledFormControl>
  );
});

DatePicker.displayName = 'DatePicker';

export default DatePicker;